import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Circle, Lock, Share2, Sparkles } from 'lucide-react';
import { UserRank, RANK_TIERS, getRankProgress } from '../lib/rankCalculator';

interface RankDetailSheetProps {
  rank: UserRank;
  objectsDiscovered: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function RankDetailSheet({ rank, objectsDiscovered, isOpen, onClose }: RankDetailSheetProps) {
  const { pct, remaining } = getRankProgress(rank, objectsDiscovered);
  const isMax = rank.maxObjects === null;
  const currentIdx = RANK_TIERS.findIndex(t => t.key === rank.key);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My COMENIUS Rank', text: rank.shareCopy, url: window.location.origin });
      } catch {}
    } else {
      try { await navigator.clipboard.writeText(rank.shareCopy); } catch {}
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          />

          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Rank details and journey overview"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[90vh] overflow-hidden rounded-t-[28px] bg-[#0f1c30] border-t border-white/10 flex flex-col focus-ring"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 bg-white/30 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-2 pb-4 shrink-0">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-xl"
                  style={{ background: `${rank.color}20`, border: `1px solid ${rank.color}40` }}
                >
                  {rank.emoji}
                </div>
                <div>
                  <p className="text-base font-bold" style={{ color: rank.color }}>{rank.title}</p>
                  <p className="text-xs text-white/80">{rank.tagline}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Close rank details"
                className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:text-white transition-colors active:scale-90 focus-ring touch-target"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto no-scrollbar px-6 pb-10 space-y-6">

              {/* Progress */}
              <section>
                <p className="text-xs font-black uppercase tracking-widest text-slate-300 mb-3">Progress</p>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-white tabular-nums">
                      {objectsDiscovered}
                      <span className="text-slate-300 font-normal"> / {isMax ? '∞' : rank.maxObjects}</span>
                    </span>
                    {!isMax && (
                      <span className="text-xs font-bold" style={{ color: rank.color }}>
                        {remaining} more to {RANK_TIERS[currentIdx + 1]?.title}
                      </span>
                    )}
                    {isMax && (
                      <span className="text-xs font-black uppercase tracking-widest" style={{ color: rank.color }}>
                        Max Rank ✦
                      </span>
                    )}
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-none"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, ${rank.color}, ${rank.color}88)`,
                      }}
                    />
                  </div>
                </div>
              </section>

              {/* Journey */}
              <section>
                <p className="text-xs font-black uppercase tracking-widest text-slate-300 mb-3">Your Journey</p>
                <div className="space-y-2">
                  {RANK_TIERS.map((tier, idx) => {
                    const isCompleted = idx < currentIdx;
                    const isCurrent = idx === currentIdx;
                    const isLocked = idx > currentIdx;

                    return (
                      <div
                        key={tier.key}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                          isCurrent
                            ? 'border-white/20 bg-white/[0.08]'
                            : isCompleted
                            ? 'border-transparent bg-white/[0.04]'
                            : 'border-transparent opacity-60'
                        }`}
                      >
                        {/* State icon */}
                        <div className="shrink-0">
                          {isCompleted ? (
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                              <Check size={12} className="text-emerald-400" />
                            </div>
                          ) : isCurrent ? (
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center border"
                              style={{ borderColor: `${tier.color}60`, background: `${tier.color}20` }}
                            >
                              <Circle size={8} style={{ color: tier.color, fill: tier.color }} />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                              <Lock size={12} className="text-slate-300" />
                            </div>
                          )}
                        </div>

                        {/* Emoji + name */}
                        <span className="text-base shrink-0">{tier.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-xs font-bold ${isLocked ? 'text-slate-400' : 'text-white'}`}
                          >
                            {tier.title}
                            {isCurrent && (
                              <span
                                className="ml-2 text-xs font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                                style={{ background: `${tier.color}25`, color: tier.color }}
                              >
                                Current
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-300 truncate">{tier.tagline}</p>
                        </div>

                        {/* Threshold badge */}
                        <span className="text-xs text-slate-300 font-bold tabular-nums shrink-0">
                          {tier.minObjects === 0 ? 'Start' : `${tier.minObjects}+`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Perks */}
              {rank.perks.length > 0 && (
                <section>
                  <p className="text-xs font-black uppercase tracking-widest text-slate-300 mb-3">Perks at This Tier</p>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2.5">
                    {rank.perks.map((perk) => (
                      <div key={perk} className="flex items-center gap-2.5">
                        <Sparkles size={14} style={{ color: rank.color }} className="shrink-0" />
                        <span className="text-xs text-white/90">{perk}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Share */}
              <button
                onClick={handleShare}
                aria-label="Share my rank"
                className="w-full min-h-[44px] py-3 rounded-2xl border border-white/15 bg-white/10 flex items-center justify-center gap-2 text-xs font-bold text-white hover:bg-white/15 active:scale-[0.98] transition-all focus-ring touch-target"
              >
                <Share2 size={16} />
                <span>Share My Rank</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
